/**
 * Victoria London — native HLS video carousel player.
 * Replaces the Tolstoy widget. Videos autoplay muted while visible
 * (IntersectionObserver), pause off-screen, and toggle sound on tap.
 * Safari plays HLS natively; other browsers lazy-load hls.js (light build)
 * from the theme assets only when the first video card enters view.
 */
(function () {
  var script = document.currentScript;
  var HLS_LIB = script ? script.getAttribute('data-hls-src') : null;

  function isM3u8(url) {
    return /\.m3u8(\?|$)/.test(url);
  }

  var hlsPromise = null;
  function loadHls() {
    if (!hlsPromise) {
      hlsPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = HLS_LIB;
        s.onload = function () { resolve(window.Hls); };
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return hlsPromise;
  }

  function removeIfOptimistic(video) {
    /* Optimistic sources (conventional CDN paths that may not exist yet)
       vanish quietly on failure — the poster imagery stays. */
    if (video.hasAttribute('data-vl-optimistic') && video.parentNode) {
      video.parentNode.removeChild(video);
    }
  }

  function attach(video) {
    var src = video.getAttribute('data-src');
    /* phones get the lighter rendition when one is provided */
    var mobileSrc = video.getAttribute('data-src-mobile');
    if (mobileSrc && window.matchMedia('(max-width: 812px)').matches) src = mobileSrc;
    if (!src) return;
    var start = parseFloat(video.getAttribute('data-start') || '0') || 0;
    video.addEventListener('error', function () { removeIfOptimistic(video); });
    video.addEventListener('canplay', function () {
      /* Re-attempt playback once buffered — a start-offset seek can beat the
         initial play() call and leave the video paused. Muted, so allowed. */
      if (video.paused && video.dataset.vlInit) video.play().catch(function () {});
    });
    if (start > 0) {
      /* Play only from the offset (skip non-on-model intros); when the loop
         wraps to 0, jump back to the start point. */
      video.addEventListener('loadedmetadata', function () { video.currentTime = start; });
      video.addEventListener('timeupdate', function () {
        if (video.currentTime < start - 0.3) video.currentTime = start;
      });
    }
    if (!isM3u8(src) || video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    } else if (HLS_LIB) {
      loadHls().then(function (Hls) {
        if (Hls && Hls.isSupported()) {
          var hls = new Hls({ capLevelToPlayerSize: true, maxBufferLength: 10 });
          hls.on(Hls.Events.ERROR, function (evt, data) {
            if (!data || !data.fatal) return;
            if (video.hasAttribute('data-vl-optimistic')) {
              hls.destroy();
              removeIfOptimistic(video);
            } else if (data.type === 'mediaError') {
              hls.recoverMediaError();
            } else {
              hls.destroy();
            }
          });
          hls.loadSource(src);
          hls.attachMedia(video);
        }
      }).catch(function () {});
    }
  }

  var bgIo = null;
  function bindBackgroundVideos() {
    /* Background videos (category tiles, banners): visibility-gated muted
       loops with no sound UI — clicks fall through to the tile link.
       Re-runs on shopify:section:load (theme editor / hot reload) and only
       binds videos it hasn't seen. */
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var bgVideos = [].slice.call(document.querySelectorAll('[data-vl-video-bg] video'))
      .filter(function (v) { return !v.dataset.vlObserved; });
    if (!bgVideos.length) return;
    if (!bgIo) {
      bgIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            if (!v.dataset.vlInit) {
              v.dataset.vlInit = '1';
              attach(v);
            }
            v.play().catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.2 });
    }
    bgVideos.forEach(function (v) {
      v.dataset.vlObserved = '1';
      bgIo.observe(v);
    });
  }

  function init() {
    if (!window.__vlVideoInit) {
      window.__vlVideoInit = true;
      document.addEventListener('shopify:section:load', function () {
        setTimeout(bindBackgroundVideos, 50);
      });
    }
    bindBackgroundVideos();

    var cards = [].slice.call(document.querySelectorAll('[data-vl-video-card]'));
    if (!cards.length) return;

    var videos = cards.map(function (card) { return card.querySelector('video'); }).filter(Boolean);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (!v.dataset.vlInit) {
            v.dataset.vlInit = '1';
            attach(v);
          }
          v.play().catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.35 });

    videos.forEach(function (v) { io.observe(v); });

    cards.forEach(function (card) {
      var v = card.querySelector('video');
      if (!v) return;
      card.addEventListener('click', function (evt) {
        if (evt.target.closest('a')) return; // let shoppable links through
        var unmuting = v.muted;
        videos.forEach(function (other) {
          if (other !== v) {
            other.muted = true;
            other.closest('[data-vl-video-card]').classList.remove('is-sound');
          }
        });
        v.muted = !unmuting;
        card.classList.toggle('is-sound', !v.muted);
        if (v.paused) v.play().catch(function () {});
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
